// Floating "Data Chat" widget: bottom-right launcher + panel, available on
// both the Desk and the website/portal. Answers come from a local,
// permission-checked query engine on the server - no external LLM call is
// made from here or from data_chat.api.ask.

(function () {
	const STAGE_LABELS = {
		interpreting: __("Interpreting your question…"),
		searching: __("Searching…"),
	};

	// Standard "fullscreen" / "fullscreen exit" glyphs (4 corner brackets,
	// pointing outward to expand and inward to restore).
	const EXPAND_ICON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
		<path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
	</svg>`;
	const COLLAPSE_ICON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
		<path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
	</svg>`;

	// Simple static logo: a chat bubble on a flat brand-colour circle.
	const LOGO_ICON = `<svg width="56" height="56" viewBox="0 0 56 56" xmlns="http://www.w3.org/2000/svg">
		<circle cx="28" cy="28" r="28" fill="var(--primary, #2490ef)"/>
		<path d="M15 21c0-2.2 1.8-4 4-4h18c2.2 0 4 1.8 4 4v10c0 2.2-1.8 4-4 4H24l-6.5 5v-5H19c-2.2 0-4-1.8-4-4v-10z"
			fill="#fff"/>
		<circle cx="21.5" cy="26" r="1.7" fill="var(--primary, #2490ef)"/>
		<circle cx="28" cy="26" r="1.7" fill="var(--primary, #2490ef)"/>
		<circle cx="34.5" cy="26" r="1.7" fill="var(--primary, #2490ef)"/>
	</svg>`;

	class DataChatWidget {
		constructor() {
			this.is_open = false;
			this.is_maximized = false;
			this.context = null; // last resolved {doctype, action, filters, search_term}, for follow-up questions
			this.render();
			this.bind_events();
			this.subscribe_progress();
		}

		render() {
			this.$wrapper = $(`
				<div class="data-chat-widget">
					<div class="data-chat-panel">
						<div class="data-chat-header">
							<span>${__("Ask about your data")}</span>
							<div class="data-chat-header-actions">
								<button type="button" class="data-chat-maximize" aria-label="${__("Maximize")}" title="${__("Maximize")}">${EXPAND_ICON}</button>
								<button type="button" class="data-chat-close" aria-label="${__("Close")}">&times;</button>
							</div>
						</div>
						<div class="data-chat-messages"></div>
						<form class="data-chat-input-row">
							<input type="text" class="data-chat-input" placeholder="${__("e.g. how many sales orders this month?")}" autocomplete="off">
							<button type="submit" class="data-chat-send">${__("Send")}</button>
						</form>
					</div>
					<button type="button" class="data-chat-launcher" aria-label="${__("Ask about your data")}">
						${LOGO_ICON}
					</button>
				</div>
			`).appendTo("body");

			this.$panel = this.$wrapper.find(".data-chat-panel");
			this.$messages = this.$wrapper.find(".data-chat-messages");
			this.$input = this.$wrapper.find(".data-chat-input");
			this.$maximize_btn = this.$wrapper.find(".data-chat-maximize");

			this.add_message("bot", __("Hi! Ask me a question about your data, e.g. \"how many sales orders this month\" or \"total sales invoice amount this year\"."));
		}

		bind_events() {
			this.$wrapper.find(".data-chat-launcher").on("click", () => this.toggle());
			this.$wrapper.find(".data-chat-close").on("click", () => this.toggle(false));
			this.$maximize_btn.on("click", () => this.toggle_maximize());
			this.$wrapper.find(".data-chat-input-row").on("submit", (e) => {
				e.preventDefault();
				this.send();
			});
		}

		toggle_maximize(force) {
			this.is_maximized = typeof force === "boolean" ? force : !this.is_maximized;
			this.$panel.toggleClass("data-chat-panel-maximized", this.is_maximized);
			this.$maximize_btn
				.attr("aria-label", this.is_maximized ? __("Restore") : __("Maximize"))
				.attr("title", this.is_maximized ? __("Restore") : __("Maximize"))
				.html(this.is_maximized ? COLLAPSE_ICON : EXPAND_ICON);
			this.$messages.scrollTop(this.$messages[0].scrollHeight);
		}

		subscribe_progress() {
			// Live progress ticks are a Desk-only nicety (frappe.realtime is not
			// guaranteed to be loaded on website/portal pages) - the final
			// answer always arrives via the frappe.call response regardless.
			if (window.frappe && frappe.realtime && frappe.realtime.on) {
				frappe.realtime.on("data_chat_progress", (data) => {
					if (this.$status) {
						this.$status.text(STAGE_LABELS[data.stage] || __("Working…"));
					}
				});
			}
		}

		toggle(force) {
			this.is_open = typeof force === "boolean" ? force : !this.is_open;
			this.$panel.toggleClass("data-chat-panel-open", this.is_open);
			if (this.is_open) {
				this.$input.trigger("focus");
			}
		}

		add_message(from, html_or_text, { as_html = false } = {}) {
			const $msg = $(`<div class="data-chat-msg data-chat-msg-${from}"></div>`);
			if (as_html) {
				$msg.html(html_or_text);
			} else {
				$msg.text(html_or_text);
			}
			this.$messages.append($msg);
			this.$messages.scrollTop(this.$messages[0].scrollHeight);
			return $msg;
		}

		send() {
			const question = (this.$input.val() || "").trim();
			if (!question) return;

			this.add_message("user", question);
			this.$input.val("");

			const $pending = this.add_message("bot", "");
			this.$status = $(`<span class="data-chat-status">${__("Working…")}</span>`);
			$pending.append(this.$status);

			frappe.call({
				method: "data_chat.api.ask",
				args: {
					question,
					context: this.context ? JSON.stringify(this.context) : null,
				},
				callback: (r) => {
					$pending.empty();
					const answer = r.message || {};
					if (answer.context) {
						// Only overwrite when the server resolved a doctype this
						// turn - an "I didn't understand" reply shouldn't wipe
						// out context a real follow-up could still use.
						this.context = answer.context;
					}
					this.render_answer($pending, answer);
				},
				error: () => {
					$pending.empty();
					$pending.text(__("Sorry, something went wrong answering that."));
				},
			});
		}

		render_answer($container, answer) {
			if (answer.type === "count" || answer.type === "currency") {
				$container.append($(`<div class="data-chat-stat"></div>`).text(answer.message));
				return;
			}

			if (answer.type === "table") {
				$container.append($(`<div class="data-chat-answer-msg"></div>`).text(answer.message));
				const $table = $(`<table class="data-chat-table"></table>`);
				const $head = $("<tr></tr>");
				answer.fields.forEach((f) => $head.append($(`<th></th>`).text(f)));
				$table.append($head);
				answer.rows.forEach((row) => {
					const $row = $("<tr></tr>");
					answer.fields.forEach((f) => {
						const $cell = $("<td></td>");
						if (f === "name" && this.is_desk_context()) {
							$cell.append(
								$(`<a target="_blank"></a>`)
									.attr("href", `/app/${frappe.router.slug(answer.doctype)}/${encodeURIComponent(row[f])}`)
									.text(row[f])
							);
						} else {
							$cell.text(row[f] == null ? "" : row[f]);
						}
						$row.append($cell);
					});
					$table.append($row);
				});
				$container.append($table);
				return;
			}

			if (answer.type === "empty") {
				$container.text(answer.message);
				return;
			}

			// unknown
			$container.append($(`<div></div>`).text(answer.message || __("I couldn't understand that.")));
			if (answer.examples && answer.examples.length) {
				const $ex = $(`<div class="data-chat-examples"></div>`).text(__("Try asking:"));
				const $list = $("<ul></ul>");
				answer.examples.forEach((e) => $list.append($("<li></li>").text(e)));
				$ex.append($list);
				$container.append($ex);
			}
		}

		is_desk_context() {
			return window.location.pathname.startsWith("/app");
		}
	}

	function is_logged_in() {
		// Website/portal pages (templates/base.html) stamp this attribute on
		// <body>. The Desk shell (www/app.html) does not, so fall back to
		// frappe.boot.user, which app.html sets inline before any bundle runs.
		const status = document.body.getAttribute("frappe-session-status");
		if (status) return status === "logged-in";
		return !!(
			window.frappe &&
			frappe.boot &&
			frappe.boot.user &&
			frappe.boot.user.name &&
			frappe.boot.user.name !== "Guest"
		);
	}

	function boot_widget() {
		if (!is_logged_in()) return;

		const on_desk = window.location.pathname.startsWith("/app");

		frappe.call({
			method: "data_chat.api.get_config",
			callback: (r) => {
				const config = r.message || {};
				if (!config.enabled) return;
				if (on_desk && !config.enable_on_desk) return;
				if (!on_desk && !config.enable_on_portal) return;
				new DataChatWidget();
			},
		});
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", boot_widget);
	} else {
		boot_widget();
	}
})();
